import './bootstrap.js';

/**
 * @typedef {object} Address
 * @property {string} country
 * @property {string} province
 * @property {string} zip
 *
 * @typedef {object} Collapsible
 * @property {() => void} show
 * @property {() => void} hide
 * @property {() => void} toggle
 * @property {() => void} dispose
 */
class ShippingOptions extends HTMLElement {
  /**
   * @readonly
   */
  STORAGE_ADDRESS_KEY = 'amb.delivery_address';

  /**
   * @readonly
   */
  STORAGE_RESULT_KEY = 'amb.shipping_rates';

  /**
   * @readonly
   */
  VIEW_FORM = 'form';

  /**
   * @readonly
   */
  VIEW_RESULT = 'result';

  /**
   * @readonly
   */
  ERROR_MAX_LENGTH = { zip: 20, province: 27, default: 50 };

  /**
   * @type {Address[]}
   */
  savedAddresses = [];

  /**
   * @type {Address | null}
   */
  savedAddress = null;

  /**
   * @type {{address: string, shippingRates: object[]}[]}
   */
  savedResults = [];

  /**
   * @type {any}
   */
  data = {};

  zipCodes = {};

  /**
   * @type {boolean}
   */
  initialized = false;

  /**
   * @type {HTMLElement}
   */
  root;

  /**
   * @type {HTMLFormElement}
   */
  form;

  /**
   * @type {HTMLElement}
   */
  result;

  /**
   * @type {HTMLElement}
   */
  resultContainer;

  /**
   * @type {HTMLElement}
   */
  changeLocationButton;

  // /**
  //  * @type {HTMLSelectElement}
  //  */
  // countryField;

  // /**
  //  * @type {HTMLSelectElement}
  //  */
  // provinceField;

  /**
   * @type {HTMLInputElement}
   */
  zipField;

  /**
   * @type {(event: SubmitEvent) => void}
   */
  submitHandler;

  /**
   * @type {(event: MouseEvent) => void}
   */
  changeLocationHandler;

  /**
   * @type {Collapsible}
   */
  formCollapse;

  /**
   * @type {Collapsible}
   */
  resultCollapse;

  constructor() {
    super();

    this.initData();
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    } else {
      this.initialized = true;
    }

    this.render();
    this.root = this.querySelector('.shipping-options');

    if (!this.root) {
      console.error('ShippingOptions: Root element not found after render.');
      return;
    }

    this.form = this.root.querySelector('form[name="shipping_options"]');
    this.resultContainer = this.root.querySelector('.shipping-options__result');
    this.changeLocationButton = this.root.querySelector('.shipping-options__change-location');
    this.countryField = this.root.querySelector('select[name="shipping_address[country]"]')
    this.provinceField = this.root.querySelector('select[name="shipping_address[province]"]')
    this.zipField = this.root.querySelector('input[name="shipping_address[zip]"]')

    if (
      !this.form ||
      !this.resultContainer ||
      !this.changeLocationButton ||
      !this.countryField ||
      !this.provinceField ||
      !this.zipField
    ) {
      console.error('ShippingOptions: Required internal elements not found.');
      return;
    }

    // Initialize Collapse instances
    try {
      this.formCollapse = new Collapse(this.form, { parent: this.root, toggle: false });
      this.resultCollapse = new Collapse(this.resultContainer, { parent: this.root, toggle: false });
    } catch (error) {
      console.error('ShippingOptions: Failed to initialize `Collapse`.', error);
    }

    this.initCountrySelect();
    this.initView();
    this.registerEvents();
  }

  /**
   * Clean up event listeners and dispose initalized `Collapse` instances to prevent memory leaks
   */
  disconnectedCallback() {
    if (this.form && this.submitHandler) {
      this.form.removeEventListener('submit', this.submitHandler);
    }

    if (this.changeLocationButton && this.changeLocationHandler) {
      this.changeLocationButton.removeEventListener('click', this.changeLocationHandler);
    }

    if (this.provinceField && this.provinceChangeHandler) {
      this.provinceField.removeEventListener('input', this.provinceChangeHandler);
    }

    if (this.zipField && this.zipChangeHandler) {
      this.zipField.removeEventListener('input', this.zipChangeHandler);
    }

    this.formCollapse.dispose();
    this.resultCollapse.dispose();

    // Allow re-initialization if re-added to DOM
    this.initialized = false;
  }

  initData() {
    const dataScript = this.querySelector('script[name="data"][type="application/json"]');

    if (!dataScript || !dataScript.textContent) {
      console.error('ShippingOptions: Data not found or empty.');
      this.data = { customer: {}, translations: {}, assets: {} };
      return;
    }

    try {
      this.data = JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('ShippingOptions: Failed to parse data script JSON.', error);
      this.data = { customer: {}, translations: {}, assets: {} };
    }

    const { is_logged_in: isLoggedIn, delivery_address: deliveryAddress } = this.data.customer;

    if (!Array.isArray(deliveryAddress)) {
      this.data.customer.delivery_address = [];
    }

    try {
      if (isLoggedIn && deliveryAddress.length > 0) {
        this.savedAddresses = deliveryAddress;
        localStorage.setItem(this.STORAGE_ADDRESS_KEY, JSON.stringify(deliveryAddress));
      } else {
        this.savedAddresses = JSON.parse(localStorage.getItem(this.STORAGE_ADDRESS_KEY) || 'null');
      }
    } catch (error) {
      console.error('ShippingOptions: Unable to parse data from localStorage, error:', error);
      this.savedAddresses = null;
    } finally {
      if (!Array.isArray(this.savedAddresses)) {
        this.savedAddresses = this.savedAddresses ? [this.savedAddresses] : [];
      }

      this.savedAddress = this.savedAddresses.find(address => address.country === this.data.default_country) || null;
    }
  }

  initView() {
    if (!this.data.requires_shipping) {
      this.closest('.product__accordion').classList.add('hidden');
      return;
    }

    this.hideFeedbacks();

    // Check for cached results for the determined address
    const addressKey = this.getAddressKey();
    const lineItemsKey = this.getLineItemsKey();
    const cachedResult = addressKey ? this.savedResults.find(({ address, lineItems }) => address === addressKey && lineItems === lineItemsKey) : undefined;
    const shippingRates = cachedResult?.shippingRates;

    const formData = new FormData(this.form);
    const isValid = [...formData.values()].find(value => !value) === undefined;

    if (Array.isArray(shippingRates) && shippingRates.length > 0) {
      this.renderResult(shippingRates);
      this.setView(this.VIEW_RESULT);
    } else if (cachedResult) {
      this.renderMessages({ result: [this.translate('products.product.shipping_options.no_result')] });
      this.setView(this.VIEW_FORM);
    } else if (isValid) {
      this.fetchShippingRates();
    } else {
      this.setView(this.VIEW_FORM);
    }
  }

  /**
   * @param {object} data
   */
  setData(data) {
    this.data = data;

    this.initView();
  }

  /**
   * Creates a stable, order-independent key from an address object.
   * @param {Address | null | undefined} address
   * @returns {string | null}
   */
  getAddressKey(address = this.savedAddress) {
    if (address && Object.keys(address).length >= 2) {
      return Object.keys(address).sort().map(key => address[key]).join('|');
    }

    return null;
  }

  getLineItemsKey() {
    const lineItems = this.data.line_items.map((variantId, index) => `${variantId}-${this.data.quantities[index]}`);

    if (!this.data.line_items.includes(this.data.variant_id)) {
      lineItems.push(`${this.data.variant_id}-1`);
    }

    return lineItems.sort().join('|');
  }

  initCountrySelect() {
    const {
      customer: { is_logged_in, delivery_address } = {},
      default_country,
      section_id
    } = this.data;
    let addressToUse = null;

    const deliveryAddress = delivery_address.find(address => address.country === default_country);

    // Determine the address to use with clear precedence
    if (is_logged_in && deliveryAddress && Object.keys(deliveryAddress).length > 0) {
      // 1. Use the saved delivery address saved in customer profile
      addressToUse = deliveryAddress;

      // Save/update local storage if different from current address
      this.saveAddress(addressToUse);
    } else if (this.savedAddress && Object.keys(this.savedAddress).length > 0) {
      // 2. Use the saved delivery address in local storage
      //    and save the address to customer profile
      addressToUse = this.savedAddress;

      this.saveAddress(addressToUse);
    } else if (default_country) {
      // 3. Use default country from current market
      addressToUse = { country: default_country, province: '', zip: '' };
    }

    // Populate form fields if an address was determined
    if (addressToUse && this.form) {
      for (const [field, value = ''] of Object.entries(addressToUse)) {
        const fieldSelector = `#address_${field}-${section_id}`;
        const fieldEl = this.form.querySelector(fieldSelector);

        if (fieldEl) {
          if (field === 'zip') {
            fieldEl.value = value;
          } else {
            // For `country` and `province`, set the `data-default` for `CountryProvinceSelector` initialize
            fieldEl.setAttribute('data-default', value);
          }
        } else {
          console.warn(`ShippingOptions: Field element not found for selector: ${fieldSelector}`);
        }
      }
    }

    const zipCodes = this.asset('zipcodes');

    try {
      new Shopify.CountryProvinceSelector(
        `address_country-${section_id}`,
        `address_province-${section_id}`,
        { hideElement: `address_province_container-${section_id}` }
      );

      if (zipCodes || zipCodes === false) {
        this.form.querySelector(`#address_province_container-${section_id}`).classList.add('hidden');
      }
    } catch (error) {
      console.error('ShippingOptions: Failed to initialize Shopify.CountryProvinceSelector.', error);
    }
  }

  registerEvents() {
    this.submitHandler = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.submitHandler);

    this.changeLocationHandler = () => this.setView(this.VIEW_FORM);
    this.changeLocationButton.addEventListener('click', this.changeLocationHandler);

    this.countryChangeHandler = this.onCountryChange.bind(this);
    this.countryField.addEventListener('input', this.countryChangeHandler);

    this.provinceChangeHandler = this.onProvinceChange.bind(this);
    this.provinceField.addEventListener('input', this.provinceChangeHandler);

    this.zipChangeHandler = this.onZipChange.bind(this);
    this.zipField.addEventListener('input', this.zipChangeHandler);
  }

  /**
   * @param {typeof VIEW_FORM | typeof VIEW_RESULT} name
   */
  setView(name) {
    this.root.setAttribute('view', name);

    switch (name) {
      case this.VIEW_FORM:
        this.formCollapse.show();

        break;
      case this.VIEW_RESULT:
        this.resultCollapse.show();

        break;
      default:
        console.error(`ShippingOptions: Unknown view: ${name}`);
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  async onSubmit(event) {
    event.preventDefault();

    await this.fetchShippingRates();
  }

  /**
   * @param {Event} event
   */
  onCountryChange(event) {
    this.hideFeedbacks();
  }

  /**
   * @param {Event} event
   */
  onProvinceChange(event) {
    this.hideFeedbacks();
  }

  /**
   * @param {Event} event
   */
  onZipChange(event) {
    event.target
      .closest('.form__group')
      .querySelectorAll('.form__message:not(.hidden)')
      .forEach(formMessage => formMessage.classList.add('hidden'))
      ;

    this.form
      .querySelectorAll('.shipping-options__messages-wrapper:not(.hidden)')
      .forEach(formMessage => formMessage.classList.add('hidden'))
      ;
  }

  /**
   *
   * @param {Address} address
   *
   * @returns {boolean}
   */
  isSavedAddress(address) {
    const addressKey = this.getAddressKey(address);

    return this.savedAddresses.some(savedAddress => {
      return this.getAddressKey(savedAddress) === addressKey;
    });
  }

  /**
   * Saves address to instance property and localStorage. Optionally persists to backend.
   * @param {Address} address
   */
  async saveAddress(address) {
    const {
      customer: {
        is_logged_in: isLoggedIn,
        delivery_address: deliveryAddress
      } = {}
    } = this.data;

    const isSavedAddress = this.isSavedAddress(address);
    let addressesString;

    this.savedAddress = address;

    if (!isSavedAddress) {
      const savedIndex = this.savedAddresses.findIndex(savedAddress => savedAddress.country === address.country);

      if (savedIndex === -1) {
        this.savedAddresses.push(address);
      } else {
        this.savedAddresses.splice(savedIndex, 1, address);
      }

      addressesString = JSON.stringify(this.savedAddresses);
      try {
        localStorage.setItem(this.STORAGE_ADDRESS_KEY, addressesString);
      } catch (error) {
        console.error('ShippingOptions: Failed to save address to localStorage.', error);
      }
    }

    // Persist to server if requested and user is logged in
    if (isLoggedIn && !isSavedAddress) {
      try {
        const response = await fetch('/apps/api/customer/delivery-address', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: addressesString
        });

        if (!response.ok) {
          let errorBody = 'Could not read error response.';

          try {
            errorBody = await response.text();
          } catch { }

          console.error(`ShippingOptions: Failed to persist address. Status: ${response.status}. Body: ${errorBody}`);
        } else {
          this.data.customer.delivery_address = this.savedAddress;
        }
      } catch (error) {
        console.error('ShippingOptions: Network error while persisting address.', error);
      }
    }
  }

  /**
    * Saves shipping rates results to instance property and localStorage for the current savedAddress.
    * @param {object[]} shippingRates - The array of rates to save.
    */
  saveResult(shippingRates = []) {
    const addressKey = this.getAddressKey(this.savedAddress);
    const lineItemsKey = this.getLineItemsKey();

    if (addressKey) {
      const savedIndex = this.savedResults.findIndex(result => result.address === addressKey && result.lineItems === lineItemsKey);
      const updatedItem = { address: addressKey, lineItems: lineItemsKey, timestamp: Date.now(), shippingRates };

      if (savedIndex === -1) {
        this.savedResults.unshift(updatedItem);
      } else {
        // Move updated result to the beginning (most recently used)
        this.savedResults.splice(savedIndex, 1);
        this.savedResults.unshift(updatedItem);
      }
    }
  }

  async fetchShippingRates() {
    if (!this.data.available) {
      this.renderMessages({
        result: [this.translate('products.product.shipping_options.unavailable')]
      }, 'error');
      this.setView(this.VIEW_FORM);

      return;
    }

    const submitButton = this.form.querySelector('button[type="submit"]');
    const formData = Object.fromEntries(new FormData(this.form));
    const country = this.data.default_country;
    const zip = formData['shipping_address[zip]'];
    let lineItem;

    submitButton.classList.add('loading');

    if (!this.zipCodes[country] && this.asset('zipcodes')) {
      try {
        const response = await fetch(this.asset('zipcodes'));
        const zipcodes = await response.json();
        this.zipCodes[country] = zipcodes;
      } catch (error) {
        console.error('ShippingOptions: Failed to fetch zipcodes.', error);
        this.renderMessages({
          result: [this.translate('products.product.shipping_options.invalid_zip', { country: this.data.default_country })]
        }, 'error');

        submitButton.classList.remove('loading');
        this.setView(this.VIEW_FORM);
        return;
      }
    }

    if (this.zipCodes[country]) {
      const state = Object.keys(this.zipCodes[country]).find(state => this.zipCodes[country][state].includes(zip));

      if (!state) {
        this.renderMessages({
          result: [this.translate('products.product.shipping_options.invalid_zip', { country: this.data.default_country })]
        }, 'error');

        submitButton.classList.remove('loading');
        return this.setView(this.VIEW_FORM);
      } else {
        this.provinceField.value = state;
        this.provinceField.dataset.state = state;
      }
    } else {
      delete this.provinceField.dataset.state;
    }

    this.hideFeedbacks();

    const atcButton = document.querySelector('.product-form__buttons button[type="submit"]');

    if (!this.data.line_items.includes(this.data.variant_id)) {
      atcButton.setAttribute('shipping-processing', true);
      atcButton.classList.add('disabled');

      lineItem = await this.addToCart(this.data.variant_id);
    }

    if (lineItem && !lineItem.requires_shipping) {
      this.renderMessages({ result: [this.translate('products.product.shipping_options.not_required')] });
      this.setView(this.VIEW_FORM);

      this.removeFromCart(lineItem);

      submitButton.classList.remove('loading');
      atcButton.removeAttribute('shipping-processing');
      atcButton.classList.remove('disabled');
      return;
    }

    let searchParams, originErrors, originShippingRates;

    try {
      searchParams = new URLSearchParams(formData);

      if (this.asset('zipcodes') === false) {
        searchParams.delete('shipping_address[province]');
      } else {
        searchParams.set('shipping_address[province]', this.provinceField.dataset.state || this.provinceField.value);
      }

      const response = await fetch(`/cart/shipping_rates.json?${searchParams.toString()}`);
      const { shipping_rates: shippingRates, ...errors } = await response.json();

      if (response.ok) {
        originShippingRates = shippingRates;
      } else {
        originErrors = errors;
      }
    } catch (error) {
      console.error('ShippingOptions: An error occurred fetching shipping rates.', error);
    } finally {
      submitButton.classList.remove('loading');

      if (lineItem) {
        this.removeFromCart(lineItem);
      }

      atcButton.removeAttribute('shipping-processing');
      atcButton.classList.remove('disabled');
    }

    window.searchParams = searchParams;
    // Extract structured address from form data
    const address = [...searchParams.keys()].reduce((address, key) => {
      address[key.replace(/^shipping_address\[(.*?)\]/, '$1')] = searchParams.get(key);

      return address;
    }, {});

    // Ensure data is an array before mapping
    const shippingRates = (Array.isArray(originShippingRates) ? originShippingRates : [])
      .map(({ name, presentment_name, price, delivery_days, currency, description }) => {
        return { name, presentment_name, price, delivery_days, currency, description };
      })
      ;

    if (!originErrors) {
      // Save address only on successful fetch without API errors
      this.saveAddress(address);
    }

    if (originErrors && Object.keys(originErrors).length > 0) {
      this.renderMessages(originErrors);
      this.setView(this.VIEW_FORM);
    } else if (!Array.isArray(shippingRates) || shippingRates.length === 0) {
      this.renderMessages({ result: [this.translate('products.product.shipping_options.no_result')] });
      this.setView(this.VIEW_FORM);
    } else {
      this.saveResult(shippingRates);
      this.renderResult(shippingRates);
      this.setView(this.VIEW_RESULT);
    }
  }

  async addToCart(variantId) {
    const response = await fetch(`/cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });

    if (response.ok) {
      return await response.json();
    } else {
      // handle errors
    }

    return null;
  }

  async removeFromCart(lineItem) {
    const response = await fetch(`/cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: lineItem.key, quantity: 0 })
    });

    if (response.ok) {
      return await response.json();
    } else {
      // handle errors
    }

    return null;
  }

  hideFeedbacks() {
    this.form
      .querySelectorAll('.form__message:not(.hidden), .shipping-options__messages-wrapper:not(.hidden)')
      .forEach(el => el.classList.add('hidden'))
      ;
  }

  render() {
    if (!this.querySelector('.shipping-options')) {
      this.root = document.createElement('div');
      this.root.classList.add('shipping-options');
      this.root.setAttribute('view', this.VIEW_FORM);
      this.appendChild(this.root);
    } else {
      this.root = this.querySelector('.shipping-options');
    }

    this.root.innerHTML = html`
      <div class="shipping-options__header">
        <span class="shipping-options__info">${this.translate('products.product.shipping_options.info', { zip: '{zip}' })}</span>

        <a class="shipping-options__change-location" role="button">${this.translate('products.product.shipping_options.change_location')}</a>
      </div>

      <form name="shipping_options" class="shipping-options__form collapse">
        <div id="address_country_container-${this.data.section_id}" class="form__group shipping-options__country-wrapper hidden">
          <div class="field select">
            <select
              id="address_country-${this.data.section_id}"
              name="shipping_address[country]"
              class="select__select field__select"
              data-default="${this.data.default_country}"
              aria-label="${this.translate('customer.addresses.country')}"
            >
              ${this.template('country-option-tags') || `<option value="">${this.translate('customer.addresses.country')}...</option>`}
            </select>
            <label class="field__label" for="address_country-${this.data.section_id}">
              ${this.translate('customer.addresses.country')}
            </label>
            ${this.template('icon-caret') || ''}
          </div>
        </div>

        <div id="address_province_container-${this.data.section_id}" class="form__group shipping-options__state-wrapper" style="display: none;">
          <div class="field select">
            <select id="address_province-${this.data.section_id}"
              name="shipping_address[province]"
              class="select__select field__select"
              data-default=""
              aria-label="${this.translate('customer.addresses.province')}"
              aria-describedby="address_province-error-${this.data.section_id}"
              aria-invalid="false"
            ></select>
            <label class="field__label" for="address_province-${this.data.section_id}">
              ${this.translate('customer.addresses.province')}
            </label>
            ${this.template('icon-caret') || ''}
          </div>
          <div id="address_province-error-${this.data.section_id}" class="form__message hidden" role="alert">
            ${this.template('icon-error') || ''}
            <small class="feedback-message" data-level="error"></small>
          </div>
        </div>

        <div id="address_zip_container-${this.data.section_id}" class="form__group shipping-options__postcode-wrapper">
          <div class="field">
            <input
              id="address_zip-${this.data.section_id}"
              class="field__input"
              type="text"
              name="shipping_address[zip]"
              placeholder="${this.translate('customer.addresses.zip')}"
              aria-label="${this.translate('customer.addresses.zip')}"
              aria-describedby="address_zip-error-${this.data.section_id}"
              aria-invalid="false"
            >
            <label class="field__label" for="address_zip-${this.data.section_id}">
              ${this.translate('customer.addresses.zip')}
            </label>
          </div>
          <div id="address_zip-error-${this.data.section_id}" class="form__message hidden" role="alert">
             ${this.template('icon-error') || ''}
            <small class="feedback-message" data-level="error"></small>
          </div>
        </div>

        <div class="shipping-options__submit-wrapper">
           <button type="submit" class="button button--full-width button--secondary">
            <span>${this.translate('customer.addresses.calculate_shipping_rates')}</span>
            <div class="loading__spinner hidden" role="status" aria-label="${this.translate('accessibility.loading')}">
               <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
               </svg>
             </div>
          </button>
        </div>

        <div class="form__group shipping-options__messages-wrapper" role="alert"></div>
      </form>

      <div class="shipping-options__result collapse"></div>
    `;
  }

  /**
   * @param {object[]} shippingRates
   */
  renderResult(shippingRates) {
    const fragment = document.createDocumentFragment();
    const headerInfo = this.root.querySelector('.shipping-options__info');

    if (headerInfo) {
      headerInfo.textContent = this.translate('products.product.shipping_options.info', { zip: this.savedAddress.zip });
    }

    for (const shippingRate of shippingRates) {
      const {
        name = 'N/A',
        presentment_name: presentmentName = name,
        delivery_days = [],
        description = '',
        price = '0',
        currency = 'USD'
      } = shippingRate;
      const priceValue = parseFloat(price) || 0;

      // Formatting price
      const formattedPrice = priceValue === 0
        ? this.translate('products.product.shipping_options.free')
        : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceValue)
        ;

      // Formatting delivery days
      let formattedShippingDays = description || '';

      if (Array.isArray(delivery_days) && delivery_days.length === 2 && !isNaN(parseInt(delivery_days[0])) && !isNaN(parseInt(delivery_days[1]))) {
        formattedShippingDays = this.translate('products.product.shipping_options.shipping_days', { min: delivery_days[0], max: delivery_days[1] });
      }

      const shippingOption = this.template('shipping-option', 'node');

      const titleEl = shippingOption.querySelector('.shipping-option__title');
      const costEl = shippingOption.querySelector('.shipping-option__cost');
      const descEl = shippingOption.querySelector('.shipping-option__description');

      if (titleEl) titleEl.textContent = presentmentName;
      if (costEl) costEl.textContent = formattedPrice;
      if (descEl) descEl.textContent = formattedShippingDays;

      fragment.appendChild(shippingOption);
    }

    this.resultContainer.innerHTML = '';
    this.resultContainer.appendChild(fragment);
  }

  /**
   * Renders error messages, either inline or in a general area.
   * @param {{[k: string]: string[]}} messages - Error object (e.g., { zip: ["can't be blank"] }).
   * @param {string} [level='error'] - The severity level of the messages (default is 'error').
   */
  renderMessages(messages, level = 'error') {
    const allMessages = messages;

    const isShortEnough = Object.keys(allMessages).every(field => {
      const messages = allMessages[field];

      return Object.hasOwn(this.ERROR_MAX_LENGTH, field) &&
        messages.length === 1 &&
        messages[0].length <= (this.ERROR_MAX_LENGTH[field] || this.ERROR_MAX_LENGTH.default)
        ;
    });

    if (isShortEnough) {
      for (const [field, errors] of Object.entries(allMessages)) {
        const formElement = this.form.querySelector(`#address_${field}-${this.data.section_id}`);
        const formMessage = formElement.closest('.form__group')?.querySelector('.form__message');

        formMessage.querySelector('.feedback-message').textContent = errors.join('');
        formMessage.classList.remove('hidden');
      }
    } else {
      const fragment = document.createDocumentFragment();
      const errorWrapper = this.form.querySelector('.shipping-options__messages-wrapper');

      for (const [field, messages] of Object.entries(allMessages)) {
        const formMessage = document.createElement('div');
        const feedbackMessage = document.createElement('span');
        const icon = this.template(`icon-${level}`, 'node');

        formMessage.classList.add('form__message');
        formMessage.appendChild(feedbackMessage);

        if (icon) {
          formMessage.prepend(icon);
        }

        feedbackMessage.classList.add('feedback-message');
        feedbackMessage.setAttribute('data-level', level);
        feedbackMessage.textContent = messages.join('');

        fragment.appendChild(formMessage);
      }

      errorWrapper.innerHTML = '';
      errorWrapper.appendChild(fragment);
      errorWrapper.classList.remove('hidden');
    }
  }

  /**
   * Retrieves asset URL from data.
   * @param {string} name
   * @returns {string | null}
   */
  asset(name) {
    const asset = this.data?.assets?.[name];

    if (asset === undefined) {
      console.error(`ShippingOptions: Asset "${name}" not found.`);
      return null;
    }

    return asset;
  }

  /**
   * Retrieves template content (as string or cloned node).
   * @param {string} name - Template name attribute.
   * @param {'source' | 'node'} output - Type of output desired.
   * @returns {string | Node | null}
   */
  template(name, output = 'source') {
    const template = this.querySelector(`template[name="${name}"]`);
    if (!template) {
      console.warn(`ShippingOptions: Template "${name}" not found.`); // Warn instead of error for optional templates
      return output === 'node' ? document.createDocumentFragment() : ''; // Return empty string/fragment
    }

    return output === 'source' ? template.innerHTML : template.content.cloneNode(true);
  }

  /**
   * Retrieves translation string from data.
   *
   * @param {string} key - Translation key.
   * @param {object} [args] - Translation args
   *
   * @returns {string} - Translated string or the key itself as fallback.
   */
  translate(key, args) {
    let message = this.data?.translations?.[key];

    if (message && args) {
      message = message.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, argKey) => {
        return Object.hasOwn(args, argKey) ? args[argKey] : _
      });
    }

    return message || key;
  }
}

// Define the custom element if it hasn't been defined already
if (!customElements.get('shipping-options')) {
  customElements.define('shipping-options', ShippingOptions);
}

/**
 * A template literal tag function that adds a `vscode-highlight` class to the
 * containing element, enabling syntax highlighting in VS Code.
 *
 * ```js
 * const htmlContent = html`
 *    <div>
 *      <h1>Hello, World!</h1>
 *      <p>This is some <strong>HTML</strong> content.</p>
 *    </div>
 * `;
 *
 * document.body.innerHTML = htmlContent;
 * ```
 *
 * @param {TemplateStringsArray} strings - The static parts of the template literal.
 * @param {...any} values - The interpolated values in the template literal.
 * @returns {string} - A string representing the HTML content wrapped in a div with the 'vscode-highlight' class.
 */
function html(strings, ...values) {
  let result = '';

  for (let i = 0; i < strings.length; i++) {
    result += strings[i];

    if (i < values.length) {
      result += values[i];
    }
  }

  return result;
}